import { useQueryClient, InfiniteData, UseMutationOptions } from 'react-query';
import produce from 'immer';

import {
  useGetManualNotificationsQuery,
  GetManualNotificationsQuery,
  GetManualNotificationsQueryVariables,
  DeleteManualNotificationMutation,
  DeleteManualNotificationMutationVariables,
} from 'api';
import { useNotify } from 'hooks';

type Mutation = DeleteManualNotificationMutation;
type MutationVariables = DeleteManualNotificationMutationVariables;
type QueryData = InfiniteData<GetManualNotificationsQuery>;
type QueryVariables = GetManualNotificationsQueryVariables;

interface IOptions {
  onMutateCompleted(): void;
}

const useDeleteManualNotification = (variables: QueryVariables, options: IOptions) => {
  const queryClient = useQueryClient();
  const queryKey = useGetManualNotificationsQuery.getKey(variables);
  const notify = useNotify();

  const mutationOptions: UseMutationOptions<Mutation, unknown, MutationVariables> = {
    onMutate: async mutationArgs => {
      await queryClient.cancelQueries(queryKey);

      const baseState = queryClient.getQueryData<QueryData>(queryKey);
      const nextState =
        baseState &&
        produce(baseState, draftState => {
          draftState.pages.forEach(page => {
            const { getManualNotificationItems } = page;
            const { count, items } = getManualNotificationItems;

            getManualNotificationItems.count = count - 1;
            getManualNotificationItems.items = items.filter(item => mutationArgs.id !== item.id);
          });
        });

      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, nextState);
      }

      options.onMutateCompleted();

      return baseState;
    },
    onError: (error, mutationArgs, baseState: QueryData) => {
      if (baseState) {
        queryClient.setQueryData<QueryData>(queryKey, baseState);
      }
      notify.mutationError();
    },
  };

  return mutationOptions;
};

export default useDeleteManualNotification;
